// Shared list screen used by Ingredients / Utensils / Equipment.
// README §6.3.
//
// Each row: 44×44 checkbox, item name, (measure for ingredients),
// optional chef-hat button if a prerequisites.skills/notes entry
// references this item via *Refs[]. For ingredients, splits[] and
// alternative / choices[] render inline.

import { useNavigate } from 'react-router-dom';
import type { Cookpit } from '@cookchow/shared';
import { Button, Checkbox, ChefHat, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { useAppStore, type CookpitFile, type PrereqKind } from '../state/store';

type Ingredient = NonNullable<Cookpit.Cookpit['ingredients']>[number];
type Equipment = NonNullable<Cookpit.Cookpit['equipment']>[number];
type Utensil = NonNullable<Cookpit.Cookpit['utensils']>[number];

export interface PrereqListProps {
  kind: PrereqKind;
  heading: string;
}

// The schema-derived `*Refs[]` field name on PrerequisiteItem differs by
// resource. The chef-hat appears iff any prerequisites.skills[] OR
// prerequisites.notes[] item references this id via the matching list.
function refsFieldFor(kind: PrereqKind): 'ingredientRefs' | 'equipmentRefs' | 'utensilRefs' {
  if (kind === 'ingredients') return 'ingredientRefs';
  if (kind === 'equipment') return 'equipmentRefs';
  return 'utensilRefs';
}

function buildChefHatLookup(recipe: Readonly<CookpitFile>, kind: PrereqKind): Map<string, string> {
  // Maps `itemId` → first matching `skill|note` id, so taps can deep-link
  // to the Notes screen at `#<noteOrSkillId>`.
  const out = new Map<string, string>();
  const prereqs = recipe.cookpit.prerequisites;
  if (!prereqs) return out;
  const field = refsFieldFor(kind);
  const buckets = [prereqs.skills, prereqs.notes];
  for (const bucket of buckets) {
    if (!bucket) continue;
    for (const entry of bucket) {
      const refs = (entry as unknown as Record<string, unknown>)[field];
      if (!Array.isArray(refs)) continue;
      for (const ref of refs) {
        if (typeof ref === 'string' && !out.has(ref)) {
          out.set(ref, entry.id);
        }
      }
    }
  }
  return out;
}

function formatMeasure(ing: Ingredient): string | null {
  const q = ing.quantity;
  const unit = ing.unit;
  if (q == null) return unit ?? null;
  let qStr: string;
  if (typeof q === 'number') {
    qStr = String(q);
  } else {
    qStr = `${q.min}–${q.max}`;
  }
  return unit ? `${qStr} ${unit}` : qStr;
}

function itemText(item: Ingredient | Equipment | Utensil): string {
  return item.text;
}

function ingredientAlternativesAndChoices(ing: Ingredient): string[] {
  const out: string[] = [];
  if (ing.alternative) {
    out.push(`or ${ing.alternative.text}`);
  }
  if (ing.choices) {
    for (const c of ing.choices) {
      out.push(`or ${c.text}`);
    }
  }
  return out;
}

function ingredientSplits(ing: Ingredient): string[] {
  if (!ing.splits) return [];
  return ing.splits.map((s) => {
    const pct = Math.round(s.fraction * 100);
    return s.label ? `${pct}% — ${s.label}` : `${pct}%`;
  });
}

export default function PrereqList({ kind, heading }: PrereqListProps) {
  const navigate = useNavigate();
  const recipe = useAppStore((s) => s.recipe);
  const checked = useAppStore((s) => s.checked);
  const toggleCheck = useAppStore((s) => s.toggleCheck);

  if (!recipe) {
    return (
      <main style={{ padding: '1rem' }}>
        <Wordmark size="md" as="h1" />
        <p style={{ color: theme.text.muted }}>Pick a recipe first.</p>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </main>
    );
  }

  const items = (recipe.cookpit[kind] ?? []) as Array<Ingredient | Equipment | Utensil>;
  const total = items.length;
  const done = Object.values(checked[kind]).filter(Boolean).length;
  const chefHatLookup = buildChefHatLookup(recipe, kind);

  return (
    <main
      style={{
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        maxWidth: '40rem',
        margin: '0 auto',
        minHeight: '100vh',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <Wordmark size="sm" as="span" />
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>{heading}</h1>
        <div style={{ color: theme.text.muted, fontSize: '0.9rem' }}>
          {done} of {total} ticked
        </div>
        <div
          role="progressbar"
          aria-label={`${heading} progress: ${done} of ${total} ticked`}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          style={{
            height: '6px',
            background: theme.bg.muted,
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: total === 0 ? '0%' : `${(done / total) * 100}%`,
              background: theme.accent.calm,
            }}
          />
        </div>
      </header>

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        {items.map((item) => {
          const isChecked = !!checked[kind][item.id];
          const noteId = chefHatLookup.get(item.id);
          const measure = kind === 'ingredients' ? formatMeasure(item as Ingredient) : null;
          const alts =
            kind === 'ingredients' ? ingredientAlternativesAndChoices(item as Ingredient) : [];
          const splits = kind === 'ingredients' ? ingredientSplits(item as Ingredient) : [];
          const equipmentPower = kind === 'equipment' ? ((item as Equipment).power ?? null) : null;
          return (
            <li
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.5rem',
                background: theme.bg.raised,
                borderRadius: '8px',
                minHeight: '64px',
              }}
            >
              <Checkbox
                state={isChecked ? 'checked' : 'unchecked'}
                label={`Tick ${itemText(item)}`}
                onChange={() => toggleCheck(kind, item.id)}
              />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                <div style={{ fontWeight: 500 }}>{itemText(item)}</div>
                {equipmentPower && equipmentPower !== 'none' && (
                  <div style={{ fontSize: '0.8rem', color: theme.text.muted }}>
                    {equipmentPower}
                  </div>
                )}
                {alts.map((alt) => (
                  <div key={alt} style={{ fontSize: '0.85rem', color: theme.text.muted }}>
                    {alt}
                  </div>
                ))}
                {splits.length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: theme.text.muted }}>
                    Splits: {splits.join(', ')}
                  </div>
                )}
              </div>
              {measure && (
                <div
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: theme.text.muted,
                    fontSize: '0.9rem',
                  }}
                >
                  {measure}
                </div>
              )}
              {noteId && (
                <ChefHat
                  ariaLabel={`Notes for ${itemText(item)}`}
                  onClick={() => navigate(`/notes#${noteId}`)}
                />
              )}
            </li>
          );
        })}
      </ul>

      <div>
        <Button variant="secondary" onClick={() => navigate('/prereqs')}>
          Back to Prerequisites
        </Button>
      </div>
    </main>
  );
}
